<template>
  <v-dialog
    v-model="open"
    max-width="640px"
    persistent
    :attach="true"
    eager
    content-class="new-course-alert-dialog"
  >
    <v-card v-if="alerts.length">
      <v-toolbar dark color="primary px-2">
        <v-toolbar-title>New Course Alert</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="dismiss" aria-label="Close"><v-icon>mdi-close</v-icon></v-btn>
      </v-toolbar>

      <v-divider></v-divider>

      <v-card-text class="pa-4 alert-body">
        <div
          v-for="(alert, index) in alerts"
          :key="alert.id"
          :class="index < alerts.length - 1 ? 'mb-5' : ''"
        >
          <div class="d-flex align-center mb-2">
            <v-chip
              small
              label
              :color="alert.type === 'Update' ? 'orange' : 'success'"
              text-color="white"
              class="mr-2 font-weight-bold"
            >
              {{ alert.type === 'Update' ? 'UPDATED' : 'NEW' }}
            </v-chip>
            <h3 class="course-name">{{ alert.course_name }}</h3>
          </div>
          <div class="alert-description" v-html="alert.description" @click="handleLinkClick"></div>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="justify-end pa-4">
        <v-btn color="primary" @click="dismiss">Got it</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import courseAPI from '@/api/course.js';
import {
  isTrialCompanyAdmin,
  shouldShowOnboardingOnLogin,
} from '@/utils/onboarding.js';

export default {
  name: 'NewCourseAlertDialog',

  data: () => ({
    open: false,
    alerts: [],
  }),

  mounted() {
    this.load();
  },

  methods: {
    load() {
      if (!this.$store.getters.isLoggedIn) return;

      // A trial admin still onboarding has never seen the library, so a "new
      // course" announcement means nothing to them yet, and on a first login
      // it landed on top of the welcome screen (Steve's first screen, 16 Sep
      // 2026). Hold it until the setup wizard is done (the tour is optional
      // since 18 Sep, so it no longer gates this); the next load after that
      // shows it as normal. Paid accounts are untouched.
      const g = this.$store.getters;
      if (isTrialCompanyAdmin(g) && shouldShowOnboardingOnLogin()) return;

      courseAPI
        .getActiveCourseUpdateNotifications()
        .then(response => {
          const payload = response && response.data ? response.data.data : null;
          if (!Array.isArray(payload) || payload.length === 0) return;

          const seen = this.seenIds();
          this.alerts = payload.filter(alert => seen.indexOf(alert.id) === -1);
          this.open = this.alerts.length > 0;
        })
        .catch(() => {
          // Deliberately silent. This is an announcement, not a feature the
          // user asked for, so it must never interrupt a login with an error.
          // Hub has no such endpoint yet, so a 404 there is expected.
        });
    },

    userId() {
      const user = this.$store.getters.user;
      return user && user.id ? user.id : null;
    },

    seenIds() {
      const id = this.userId();
      if (!id) return [];
      const all = this.$store.getters.seenCourseAlerts || {};
      return all[id] || [];
    },

    dismiss() {
      const id = this.userId();
      if (id && this.alerts.length) {
        this.$store.commit('markCourseAlertsSeen', {
          userId: id,
          ids: this.alerts.map(alert => alert.id),
        });
      }
      this.open = false;
    },

    // Descriptions are authored HTML and may contain links. Open them in a new
    // tab rather than navigating the SPA out from under the dialog.
    handleLinkClick(event) {
      const target = event.target;
      const parent = target.parentElement;
      const isLink = target.tagName === 'A' || (parent && parent.tagName === 'A');
      if (!isLink) return;
      event.preventDefault();
      const href = target.tagName === 'A' ? target.href : parent.href;
      if (href) window.open(href, '_blank');
    },
  },
};
</script>

<style scoped>
/* Match the release-notes dialog so it clears headers and backdrops. */
.new-course-alert-dialog {
  z-index: 3005 !important;
}
.course-name {
  font-size: 1.05rem;
  line-height: 1.3;
}
.alert-body {
  max-height: 60vh;
  overflow-y: auto;
}
.alert-description >>> p {
  margin-bottom: 0.5em;
}
</style>
